import React from 'react';
import { X } from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';
import { Button, Text } from '@musa.tech/design-system';
import { WizardStepper, type OnDemandWizardFlow, type WizardStepperStep } from './WizardStepper';
import { stepVariants } from './motionVariants';

export interface WizardLayoutProps {
  steps: WizardStepperStep[];
  currentIndex: number;
  flow?: OnDemandWizardFlow;
  onClose: () => void;
  footer: React.ReactNode;
  children: React.ReactNode;
}

export const WizardLayout: React.FC<WizardLayoutProps> = ({
  steps,
  currentIndex,
  flow,
  onClose,
  footer,
  children,
}) => (
  <div className="fixed inset-0 z-50 flex flex-col bg-card">
    <div className="flex-1 overflow-y-auto">
      <div className="mx-auto flex w-full max-w-[1440px] flex-col gap-6 px-4 pb-8 pt-6 sm:px-7">
        <div className="flex items-start justify-between gap-4">
          <div className="flex flex-col gap-1">
            <Text
              as="h1"
              variant="heading"
              size="medium"
              className="font-semibold tracking-[0.0625rem] text-neutral-900"
            >
              Nova solicitação sob demanda
            </Text>
            <Text variant="body" size="small" className="text-neutral-500">
              Preencha as informações para criar uma nova solicitação de coleta.
            </Text>
          </div>

          <Button
            variant="outline"
            size="icon"
            aria-label="Fechar"
            onClick={onClose}
            className="h-9 w-9 shrink-0 border-border bg-neutral-50"
          >
            <X className="h-[17px] w-[17px]" aria-hidden="true" />
          </Button>
        </div>

        <WizardStepper steps={steps} currentIndex={currentIndex} flow={flow} />

        {/*
          Single, shared entrance animation for every step of the on-demand
          collect wizard — key on currentIndex so it replays on every step
          change (including the very first render, i.e. "entrar na tela").
          mode="wait" avoids overlap jank between steps of very different sizes
          (e.g. the pre-selection table vs. a form step).
        */}
        <AnimatePresence mode="wait">
          <motion.div
            key={currentIndex}
            variants={stepVariants}
            initial="initial"
            animate="animate"
            exit="exit"
          >
            {children}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>

    {/*
      sm:pr-28 (além do px-4/sm:px-7 original): o widget de chat (Chatwoot)
      fica fixo em right:20px/bottom:20px com z-index quase máximo
      (2147483000) — sempre por cima, brigar por z-index não resolve. Reserva
      de ~112px à direita evita que o botão principal do rodapé nasça embaixo
      da bolha (medido: sobreposição real de ~56x34px antes deste ajuste).
      Só a partir de sm: no mobile (<640px) o rodapé já é apertado demais para
      dois botões lado a lado com rótulo longo (ex. "Continuar sem
      pré-seleção") — problema de responsividade à parte, não deste ajuste;
      reservar mais espaço aqui só pioraria.
    */}
    <div className="border-t border-border bg-card px-4 py-3.5 sm:pl-7 sm:pr-28">
      <div className="mx-auto flex w-full max-w-[1440px] items-center justify-between gap-3">
        {footer}
      </div>
    </div>
  </div>
);
